import type { Issue, IssueInput } from '@panelvault/shared';
import { RefreshCw } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CoverUpload } from '../../components/CoverUpload';
import { Button } from '../../components/ui/button';
import { Checkbox, Textarea } from '../../components/ui/controls';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { ApiError } from '../../lib/api';
import { useAddCollectionItem } from '../collection/queries';
import { useRefreshIssue } from '../sources/queries';
import { useCreateIssue, useUpdateIssue } from './queries';

interface IssueFormDialogProps {
  open: boolean;
  onClose: () => void;
  seriesId: string;
  /** Omitted for a new issue. */
  issue?: Issue;
  /** Prefilled number for "add the missing one". */
  defaultNumber?: string;
  /**
   * The issue is catalogued but not owned (a gap tile was tapped): show its
   * data and add a copy on save, instead of an empty form for a new issue.
   */
  markOwned?: boolean;
}

interface FormState {
  number: string;
  title: string;
  variantName: string;
  printing: string;
  releaseDate: string;
  price: string;
  pageCount: string;
  description: string;
  coverImageId: string | null;
  addToCollection: boolean;
}

const emptyForm: FormState = {
  number: '',
  title: '',
  variantName: '',
  printing: '',
  releaseDate: '',
  price: '',
  pageCount: '',
  description: '',
  coverImageId: null,
  addToCollection: true,
};

export function IssueFormDialog({
  open,
  onClose,
  seriesId,
  issue,
  defaultNumber,
  markOwned = false,
}: IssueFormDialogProps) {
  const { t } = useTranslation();
  const ids = useId();
  const createIssue = useCreateIssue();
  const updateIssue = useUpdateIssue();
  const addCopy = useAddCollectionItem();

  const [form, setForm] = useState<FormState>(emptyForm);
  /** The values the dialog opened with, to send only what was changed. */
  const [initial, setInitial] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    const start: FormState = issue
      ? {
          number: issue.number,
          title: issue.title ?? '',
          variantName: issue.variantName ?? '',
          printing: issue.printing?.toString() ?? '',
          releaseDate: issue.releaseDate ?? '',
          price: issue.price?.toString() ?? '',
          pageCount: issue.pageCount?.toString() ?? '',
          description: issue.description ?? '',
          coverImageId: issue.coverImageId,
          addToCollection: markOwned,
        }
      : { ...emptyForm, number: defaultNumber ?? '' };
    setForm(start);
    setInitial(start);
  }, [open, issue, defaultNumber, markOwned]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const pending = createIssue.isPending || updateIssue.isPending || addCopy.isPending;

  const refresh = useRefreshIssue();
  const [refreshNote, setRefreshNote] = useState<string | null>(null);

  useEffect(() => {
    if (open) setRefreshNote(null);
  }, [open]);

  /**
   * Reloads from the source the issue was imported from. The refreshed issue
   * comes back through the invalidated series query and resets the form.
   */
  async function reload() {
    if (!issue) return;
    setRefreshNote(null);

    try {
      const result = await refresh.mutateAsync({ issueId: issue.id, includeCover: true });
      if (result.message) {
        setRefreshNote(result.message);
        return;
      }

      const parts = [
        t('issue.refreshUpdated', { count: result.updatedFields.length, source: result.source }),
      ];
      if (result.protectedFields.length > 0) {
        parts.push(t('issue.refreshProtected', { fields: result.protectedFields.join(', ') }));
      }
      if (result.coverDownloaded) parts.push(t('issue.refreshCover'));
      setRefreshNote(parts.join(' '));
    } catch (cause) {
      setRefreshNote(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  async function submit() {
    if (form.number.trim() === '') {
      setError(t('issue.numberRequired'));
      return;
    }

    const text = (value: string) => (value.trim() === '' ? null : value.trim());
    const number = (value: string) =>
      value.trim() === '' ? null : Number(value.replace(',', '.'));

    const payload: Omit<IssueInput, 'seriesId'> = {
      number: form.number.trim(),
      title: text(form.title),
      variantName: text(form.variantName),
      printing: number(form.printing),
      releaseDate: text(form.releaseDate),
      price: number(form.price),
      pageCount: number(form.pageCount),
      description: text(form.description),
      coverImageId: form.coverImageId,
    };

    try {
      if (issue) {
        // Only what was changed: every field sent counts as edited by hand
        // and is then protected from source updates.
        const patch = Object.fromEntries(
          Object.entries(payload).filter(
            ([key]) => form[key as keyof FormState] !== initial[key as keyof FormState],
          ),
        ) as Partial<typeof payload>;
        if (Object.keys(patch).length > 0) {
          await updateIssue.mutateAsync({ id: issue.id, patch });
        }
        if (form.addToCollection) {
          await addCopy.mutateAsync({ issueId: issue.id, quantity: 1, isRead: false });
        }
      } else {
        const created = await createIssue.mutateAsync({ ...payload, seriesId });
        if (form.addToCollection) {
          await addCopy.mutateAsync({ issueId: created.id, quantity: 1, isRead: false });
        }
      }
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={markOwned ? t('issue.markOwnedTitle') : issue ? t('issue.edit') : t('issue.create')}
      description={issue ? t('form.overrideHint') : undefined}
      footer={
        <>
          {issue ? (
            <Button
              variant="surface"
              className="mr-auto"
              disabled={pending || refresh.isPending}
              onClick={() => void reload()}
            >
              <RefreshCw className={refresh.isPending ? 'size-4 animate-spin' : 'size-4'} />
              {t('issue.refresh')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('form.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={pending}>
            {pending ? t('form.saving') : t('form.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('issue.number')} htmlFor={`${ids}-number`} hint={t('issue.numberHint')}>
            <Input
              id={`${ids}-number`}
              value={form.number}
              onChange={(event) => set('number', event.target.value)}
              autoFocus
            />
          </Field>

          <Field label={t('issue.variant')} htmlFor={`${ids}-variant`}>
            <Input
              id={`${ids}-variant`}
              value={form.variantName}
              onChange={(event) => set('variantName', event.target.value)}
            />
          </Field>
        </div>

        <Field label={t('issue.title')} htmlFor={`${ids}-title`}>
          <Input
            id={`${ids}-title`}
            value={form.title}
            onChange={(event) => set('title', event.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('issue.releaseDate')} htmlFor={`${ids}-release`}>
            <Input
              id={`${ids}-release`}
              type="date"
              value={form.releaseDate}
              onChange={(event) => set('releaseDate', event.target.value)}
            />
          </Field>

          <Field label={t('issue.price')} htmlFor={`${ids}-price`}>
            <Input
              id={`${ids}-price`}
              inputMode="decimal"
              value={form.price}
              onChange={(event) => set('price', event.target.value)}
            />
          </Field>

          <Field label={t('issue.printing')} htmlFor={`${ids}-printing`}>
            <Input
              id={`${ids}-printing`}
              inputMode="numeric"
              value={form.printing}
              onChange={(event) => set('printing', event.target.value)}
            />
          </Field>

          <Field label={t('issue.pageCount')} htmlFor={`${ids}-pages`}>
            <Input
              id={`${ids}-pages`}
              inputMode="numeric"
              value={form.pageCount}
              onChange={(event) => set('pageCount', event.target.value)}
            />
          </Field>
        </div>

        <CoverUpload
          label={t('form.cover')}
          imageId={form.coverImageId}
          onChange={(id) => set('coverImageId', id)}
        />

        <Field label={t('form.description')} htmlFor={`${ids}-description`}>
          <Textarea
            id={`${ids}-description`}
            value={form.description}
            onChange={(event) => set('description', event.target.value)}
          />
        </Field>

        {!issue || markOwned ? (
          <Checkbox
            label={t('issue.addToCollection')}
            checked={form.addToCollection}
            onChange={(event) => set('addToCollection', event.target.checked)}
          />
        ) : null}

        {refreshNote ? (
          <p
            role="status"
            className="rounded-lg border bg-surface-raised p-3 text-sm text-foreground"
          >
            {refreshNote}
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
